<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import FlowCardQC from '@/shared/components/FlowCardQC/FlowCardQC.vue'
  import WrapperQC from '@qicgroupdoha/qic-connect-ds/components/WrapperQC'
  import { InsuredTravelersOptions } from '@/shared/components/InsuredTravelersOptions'
  import { TravelDirection } from '@/shared/types/common.types'
  import ButtonQC from '@qicgroupdoha/qic-connect-ds/components/ButtonQC'
  import { AccordionQC } from '@/shared/components/AccordionQC'
  import {
    useGetInsuredTravelerOptions,
    type TravelerOption,
  } from '@/shared/composables/useGerInsuredTravelersOptions'

  interface TravelerWithName extends TravelerOption {
    firstName?: string
    lastName?: string
  }

  interface Emits {
    (e: 'edit'): void
    (e: 'toggle', isOpen: boolean): void
  }

  interface Props {
    travelers?: TravelerWithName[]
    typeOfTravel?: TravelDirection
    isOpen?: boolean
    isLoading?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    travelers: () => [],
    typeOfTravel: TravelDirection.OUTBOUND,
    isLoading: false,
  })

  const emit = defineEmits<Emits>()

  const { t } = useI18n()
  const { getInsuredTravelerOptions } = useGetInsuredTravelerOptions()

  const isOpen = ref(!!props.isOpen)

  const title = computed(() =>
    props.travelers.length > 1
      ? t('travellers_insured')
      : t('travel.common.quote.insured_traveler.name')
  )

  const description = computed(() =>
    props.travelers.length > 1
      ? `${t(
          'outbound.plans.policy_information.people',
          { personCount: props.travelers?.length ?? 0 },
          props.travelers?.length ?? 0
        )}`
      : `${props.travelers?.[0]?.firstName ?? ''} ${props.travelers?.[0]?.lastName ?? ''}`
  )

  watch(
    () => isOpen.value,
    (newValue) => {
      if (!newValue) return
      emit('toggle', newValue)
    }
  )
</script>

<template>
  <FlowCardQC class="insured-travelers">
    <div class="insured-travelers__wrapper" :class="{ 'is-open': isOpen }">
      <AccordionQC
        v-model:is-open="isOpen"
        data-test="insured-travelers-accordion"
        icon-position="end"
      >
        <template #header>
          <WrapperQC class="insured-travelers__header" gap="var(--spacing-5)">
            <WrapperQC
              class="insured-travelers__header-inner"
              direction="column"
              gap="var(--spacing-2)"
            >
              <div class="insured-travelers__title heading-5">
                {{ title }}
              </div>
              <div class="insured-travelers__desc text-regular">
                {{ description }}
              </div>
            </WrapperQC>
          </WrapperQC>
        </template>

        <template #content>
          <WrapperQC
            v-for="traveler in travelers"
            :key="traveler.srNo"
            class="insured-travelers__content-item"
            direction="column"
            gap="var(--spacing-4)"
          >
            <WrapperQC
              class="insured-travelers__content-header"
              direction="column"
              gap="var(--spacing-2)"
            >
              <h4 class="insured-travelers__content-title heading-5">
                {{ traveler.firstName }} {{ traveler.lastName }}
              </h4>
              <p class="insured-travelers__content-desc text-regular">
                {{
                  traveler.srNo === 'holder'
                    ? t('check_quote.policy_holder.label')
                    : traveler.relationName
                }}
              </p>
            </WrapperQC>

            <InsuredTravelersOptions
              :options="getInsuredTravelerOptions(traveler, typeOfTravel, true)"
            />
          </WrapperQC>

          <div class="insured-travelers__content-footer">
            <ButtonQC
              variant="secondary"
              icon="edit"
              wide
              data-test="insured-travelers-edit"
              :disabled="isLoading"
              @click="emit('edit')"
            >
              {{ $t('travel.common.edit.button') }}
            </ButtonQC>
          </div>
        </template>
      </AccordionQC>
    </div>
  </FlowCardQC>
</template>

<style lang="scss" scoped>
  @use '@/shared/assets/styles/mixins';

  @mixin padding-content() {
    padding: var(--spacing-8);

    @include mixins.mobile-flow {
      padding: var(--spacing-8) var(--spacing-5);
    }
  }

  .insured-travelers {
    padding: 0;
    overflow: hidden;
  }

  .insured-travelers__wrapper {
    position: relative;

    :deep(.accordion-qc__header) {
      padding: var(--spacing-8);
      border-bottom: 1px solid transparent;
      transition: border-color 0.15s ease 0.15s;

      @include mixins.mobile-flow {
        padding: var(--spacing-8) var(--spacing-5);
      }
    }

    &.is-open {
      :deep(.accordion-qc__header) {
        border-color: var(--colorBrdrSrfTertiary);
      }
    }
  }

  .insured-travelers__header {
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  .insured-travelers__content-item {
    border-bottom: 1px solid var(--colorBrdrSrfTertiary);
  }

  .insured-travelers__content-item,
  .insured-travelers__content-footer {
    padding: var(--spacing-8);
  }

  .insured-travelers__desc,
  .insured-travelers__content-desc {
    color: var(--colorTxtSrfSecondary);
  }
</style>
